import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetCompany } from "@/api/company.api";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DeliveryFormOcrData,
  DeliveryFormOcrSchema,
} from "@/types/forms/home/cotationDeliveryForm.form";
import { InfoDelivery } from "@/components/pendingShipments/finalize/infoDelivery";
import Paths from "@/constants/paths";
import {
  useGetOneExpedition,
  useUpdateValidatedQuotationImport,
  useValidateQuotationImport,
} from "@/api/expedition.api";
import { Loader2 } from "lucide-react";
import { GetAllCompanyDto, GetOneExpeditionResponseDto } from "@/types/api/api-types";
import { Button } from "@/components/ui/button";
import { InfoInvoiceOcr } from "@/components/pendingShipments/finalize/infoInvoiceOcr";
import { updateValidatedCotationOcrPayload } from "@/components/pendingShipments/finalize/updateValidatedCotationPayload";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { toast } from "react-toastify";
import { useCreateFile } from "@/api/file.api";

export const UpdateQuotationOcrPage = () => {
  const { id } = useParams();

  const { data: company, isSuccess: isCompanySuccess } = useGetCompany();
  const { data: expedition, isSuccess: isExpeditionSuccess } = useGetOneExpedition(id || "");

  if (!isExpeditionSuccess || !isCompanySuccess) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 p-8">
        <Loader2 className="text-brand700 h-10 w-10 animate-spin" />
      </div>
    );
  }

  if (
    isExpeditionSuccess &&
    expedition.deliveryStatus !== DeliveryStatusEnum.WAITING &&
    expedition.deliveryStatus !== DeliveryStatusEnum.ONGOING
  ) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 p-8">
        <h1 className="text-3xl font-semibold">
          Cette expédition ne peut pas être modifiée. Contactez Pelican Air Services en cas de
          problème.
        </h1>
      </div>
    );
  }

  if (expedition.processStatus !== ExpeditionProcessStatusEnum.PENDING_CUSTOMER) {
    return <UpdateValidatedQuotationOcrForm expedition={expedition} company={company} />;
  }

  return <UpdateQuotationOcrForm expedition={expedition} company={company} />;
};

type Props = {
  expedition: GetOneExpeditionResponseDto;
  company: GetAllCompanyDto;
};

// TODO Refactor

// Displayed when the expedition is already validated
// The invoice is not expected in useLocation state
// A new invoice can be uploaded
const UpdateValidatedQuotationOcrForm = ({ expedition, company }: Props) => {
  const navigate = useNavigate();
  const {
    mutateAsync: updateValidatedQuotationImport,
    isPending,
    isSuccess,
  } = useUpdateValidatedQuotationImport();

  const defaultBillingEntity =
    company?.billingEntities.length === 1 ? company?.billingEntities[0].id : undefined;
  const prefPickUpLocation =
    localStorage.getItem("prefPickUpLocation") ||
    company?.pickUpLocations?.find((el) => el.isDefault)?.id ||
    undefined;

  const client =
    expedition?.deliveryInfo?.company ||
    (expedition?.deliveryInfo?.firstName && expedition?.deliveryInfo?.lastName
      ? expedition?.deliveryInfo?.firstName + " " + expedition?.deliveryInfo?.lastName
      : "-");

  const methods = useForm<DeliveryFormOcrData>({
    defaultValues: {
      billingEntityId: expedition.expeditionBillingDetail?.billingEntityId || defaultBillingEntity,
      pickUpLocationId: expedition.pickupInfo?.pickupLocation?.id || prefPickUpLocation,
      comment: expedition.deliveryInfo?.comment || undefined,
      dapOption: expedition.expeditionOptions?.dapOption || false,
      express: expedition.expeditionOptions?.express || false,
      insurance: expedition.expeditionOptions?.insurance || false,
      reference: expedition.expeditionBillingDetail?.reference || undefined,
      newFilesId: [],
      removedFilesId: [],
      invoiceFileId: undefined,
    },
    resolver: zodResolver(DeliveryFormOcrSchema),
    mode: "onChange",
  });

  const submitForm: SubmitHandler<DeliveryFormOcrData> = async (data) => {
    const payload = updateValidatedCotationOcrPayload({
      expedition,
      data,
    });

    const ret = await updateValidatedQuotationImport({
      id: expedition.id,
      payload,
    });

    localStorage.setItem("prefPickUpLocation", data.pickUpLocationId);

    if (ret.status === 200 || ret.status === 201) navigate(Paths.home.validatedShipments);
  };

  return (
    <div className="flex h-fit w-full flex-1 flex-col gap-6 p-4 sm:p-8">
      <div className="flex flex-col gap-8">
        <div className="flex flex-row items-center gap-2">
          <a
            href={Paths.home.validatedShipments}
            className="text-medium align-middle text-sm text-gray-600"
          >
            Expéditions validées
          </a>
          <span className="align-middle text-[#CBD5E1]">{`>`}</span>
          <h4 className="text-semibold align-middle text-sm text-[#06F]">{client}</h4>
        </div>
        <div className="flex flex-col gap-3">
          <h1 className="text-3xl font-semibold">Expédition basée sur votre facture importée.</h1>
        </div>
      </div>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(submitForm)}
          className="flex flex-1 flex-col items-center gap-6"
        >
          <InfoInvoiceOcr documents={expedition?.expeditionDocuments?.documents || []} />
          <InfoDelivery
            pickUpLocations={company?.pickUpLocations || []}
            billingEntities={company?.billingEntities || []}
            documents={expedition?.expeditionDocuments?.documents || []}
          />
          <div className="flex w-full flex-row gap-3 md:max-w-[1000px] md:flex-row">
            <Button
              disabled={isPending || isSuccess}
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => navigate(Paths.home.validatedShipments)}
            >
              Annuler
            </Button>
            <Button
              disabled={isPending || isSuccess}
              onClick={() => methods.handleSubmit(submitForm)}
              type="submit"
              className="flex-1"
            >
              {isPending ? <Loader2 className="animate-spin" /> : "Modifier l'expédition"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

// Displayed when the expedition is not validated
// The invoice is expected in useLocation state
const UpdateQuotationOcrForm = ({ expedition, company }: Props) => {
  const {
    mutateAsync: validateQuotationImport,
    isPending,
    isSuccess,
  } = useValidateQuotationImport();
  const { mutateAsync: createFile } = useCreateFile();
  const navigate = useNavigate();
  const { invoice } = useLocation().state as { invoice: File[] };

  const defaultBillingEntity =
    company?.billingEntities.length === 1 ? company?.billingEntities[0].id : undefined;
  const prefPickUpLocation =
    localStorage.getItem("prefPickUpLocation") ||
    company?.pickUpLocations?.find((el) => el.isDefault)?.id ||
    undefined;
  const client =
    expedition?.deliveryInfo?.company ||
    (expedition?.deliveryInfo?.firstName && expedition?.deliveryInfo?.lastName
      ? expedition?.deliveryInfo?.firstName + " " + expedition?.deliveryInfo?.lastName
      : "-");

  const methods = useForm<DeliveryFormOcrData>({
    defaultValues: {
      billingEntityId: expedition.expeditionBillingDetail?.billingEntityId || defaultBillingEntity,
      pickUpLocationId: expedition.pickupInfo?.pickupLocation?.id || prefPickUpLocation,
      comment: expedition.deliveryInfo?.comment || undefined,
      dapOption: expedition.expeditionOptions?.dapOption || false,
      express: expedition.expeditionOptions?.express || false,
      insurance: expedition.expeditionOptions?.insurance || false,
      reference: expedition.expeditionBillingDetail?.reference || undefined,
      newFilesId: [],
      invoiceFileId: undefined,
    },
    resolver: zodResolver(DeliveryFormOcrSchema),
    mode: "onChange",
  });

  const submitForm: SubmitHandler<DeliveryFormOcrData> = async (data) => {
    try {
      const { uploadUrl, fileId } = await createFile({
        fileName: invoice[0].name,
        type: FileTypeEnum.EXPEDITION_INVOICE,
        contentType: invoice[0].type,
      });

      await fetch(uploadUrl, {
        method: "PUT",
        body: invoice[0],
        headers: { "Content-Type": invoice[0].type },
      });

      data.invoiceFileId = fileId;
    } catch (error) {
      console.error("Erreur lors de l'import de la facture");
      return;
    }

    if (!data.invoiceFileId) {
      toast.error(
        "Erreur lors de la validation de l'expédition. Il semble que la facture importée soit manquante. Rechargez la page et réessayez.",
      );
      return;
    }
    const ret = await validateQuotationImport({
      id: expedition.id,
      payload: {
        billingEntityId: data.billingEntityId,
        pickUpLocationId: data.pickUpLocationId,
        express: data.express,
        insurance: data.insurance,
        reference: data.reference || undefined,
        newFilesId: data.newFilesId,
        removedFilesId: data.removedFilesId,
        invoiceFileId: data.invoiceFileId,
        comment: data.comment || undefined,
      },
    });

    localStorage.setItem("prefPickUpLocation", data.pickUpLocationId);

    if (ret.status === 200 || ret.status === 201) navigate(Paths.home.pendingShipments);
  };

  return (
    <div className="flex h-fit flex-1 flex-col gap-6 p-4 sm:p-8">
      <div className="flex flex-col gap-8">
        <div className="flex flex-row items-center gap-2">
          <a
            href={Paths.home.pendingShipments}
            className="text-medium align-middle text-sm text-gray-600"
          >
            Expéditions en attente
          </a>
          <span className="align-middle text-[#CBD5E1]">{`>`}</span>
          <h4 className="text-semibold align-middle text-sm text-[#06F]">{client}</h4>
        </div>
        <div className="flex flex-col gap-3">
          <h1 className="text-3xl font-semibold">Expédition basée sur votre facture importée</h1>
        </div>
      </div>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(submitForm)}
          className="flex flex-1 flex-col items-center gap-6"
        >
          <InfoDelivery
            pickUpLocations={company?.pickUpLocations || []}
            billingEntities={company?.billingEntities || []}
            documents={expedition?.expeditionDocuments?.documents || []}
          />
          <div className="flex w-full flex-row gap-3 md:max-w-[1000px] md:flex-row">
            <Button
              disabled={isPending || isSuccess}
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => navigate(Paths.home.pendingShipments)}
            >
              Annuler
            </Button>
            <Button
              disabled={isPending || isSuccess}
              onClick={() => methods.handleSubmit(submitForm)}
              type="submit"
              className="flex-1"
            >
              {isPending ? <Loader2 className="animate-spin" /> : "Valider l'expédition"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
